import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteIcon from "@mui/icons-material/Delete";
import LaunchIcon from "@mui/icons-material/Launch";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Sidebar from "../../components/sidebar/Sidebar";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import ProposalBoard, {
  BOARD_COLUMNS,
} from "../../components/commercial-proposals/ProposalBoard";
import { HOUSE_LABEL } from "../../components/commercial-proposals/ProjectsTable";
import {
  CommercialBriefingCrmStatus,
  CommercialProposalListItem,
  House,
  ProposalStatus,
  useDeleteCommercialProposalMutation,
  useGetCrmStagesQuery,
  useGetProposalFilterOptionsQuery,
  useListCommercialProposalsQuery,
  useRetryCommercialProposalMutation,
  useUpdateProposalStatusMutation,
} from "../../store/apis/commercialProposalApi";

const BUSINESS_UNIT_LABEL: Record<string, string> = {
  BEAPOLO: "BeApolo",
  HOUSE_GROUP: "House Group",
};

const euro = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

/**
 * Listado de propuestas comerciales.
 *
 * Lee de nuestra tabla, no de Twenty: eso es lo que hace que el listado siga funcionando
 * con el CRM caído y que no se mezclen las oportunidades heredadas de HubSpot.
 *
 * La etapa de la oportunidad no está: la mueve el comercial en Twenty y una copia local
 * quedaría obsoleta en cuanto alguien arrastrase una tarjeta.
 */
const CommercialProposals: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  // El endpoint ya exige ADMIN; esto solo evita mostrar un botón que daría 403.
  const canDelete = userDb?.role === "ADMIN";

  /**
   * Listado o tablero.
   *
   * Se recuerda en localStorage porque es una preferencia de cómo trabaja cada uno, no
   * un filtro: quien lleva el tablero no quiere volver al listado cada vez que entra.
   * La lectura va en try/catch: en una ventana privada localStorage puede lanzar.
   */
  const [view, setView] = useState<"list" | "board">(() => {
    try {
      return localStorage.getItem("commercialProposalsView") === "board"
        ? "board"
        : "list";
    } catch {
      return "list";
    }
  });

  const changeView = (next: "list" | "board") => {
    setView(next);
    try {
      localStorage.setItem("commercialProposalsView", next);
    } catch {
      // Sin persistencia la vista sigue funcionando; solo se olvida al recargar.
    }
  };

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [crmStatus, setCrmStatus] = useState("");
  // "" es "todos" en cada filtro: es lo que devuelve el Select al elegir esa opción.
  const [year, setYear] = useState("");
  const [responsableComercial, setResponsableComercial] = useState("");
  const [responsablePropuesta, setResponsablePropuesta] = useState("");
  const [house, setHouse] = useState("");
  const [status, setStatus] = useState("");
  const [hideArchived, setHideArchived] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<CommercialProposalListItem | null>(null);

  // El tablero pide el maximo de una vez: una columna con media pagina dentro no es un
  // tablero, es una mentira. El tope del backend es 100, asi que a partir de ahi el
  // tablero se queda corto y habra que paginarlo o filtrar; hoy no hay tantas.
  const hasFilters =
    search !== "" ||
    businessUnit !== "" ||
    crmStatus !== "" ||
    year !== "" ||
    responsableComercial !== "" ||
    responsablePropuesta !== "" ||
    house !== "" ||
    status !== "" ||
    hideArchived;

  const clearFilters = () => {
    setSearch("");
    setBusinessUnit("");
    setCrmStatus("");
    setYear("");
    setResponsableComercial("");
    setResponsablePropuesta("");
    setHouse("");
    setStatus("");
    setHideArchived(false);
    setPage(0);
  };

  const { data: filterOptionsData } = useGetProposalFilterOptionsQuery();
  const filterOptions = filterOptionsData?.data;

  const { data, isFetching, isError, refetch } =
    useListCommercialProposalsQuery({
      page: view === "board" ? 1 : page + 1,
      pageSize: view === "board" ? 100 : pageSize,
      search: search.trim() || undefined,
      businessUnit: (businessUnit || undefined) as
        | "BEAPOLO"
        | "HOUSE_GROUP"
        | undefined,
      crmStatus: (crmStatus || undefined) as
        | CommercialBriefingCrmStatus
        | undefined,
      year: year ? Number(year) : undefined,
      responsableComercialUserId: responsableComercial || undefined,
      responsablePropuestaUserId: responsablePropuesta || undefined,
      house: (house || undefined) as House | undefined,
      // En el tablero el estado ya son las columnas: filtrarlo dejaría seis vacías. Por
      // eso el filtro solo se ve y solo se manda en el listado.
      status:
        view === "list"
          ? ((status || undefined) as ProposalStatus | undefined)
          : undefined,
      // El tablero, solo las vigentes: una propuesta sustituida por su V2 ya no está en
      // curso, y tenerla en una columna la contaría dos veces en el importe. En el
      // listado salen salvo que se pida ocultarlas, porque ahí se mira de dónde viene
      // la vigente.
      onlyCurrent: view === "board" || hideArchived ? true : undefined,
    });

  const [moveProposal] = useUpdateProposalStatusMutation();
  const [movingIds, setMovingIds] = useState<Set<string>>(new Set());

  /**
   * Mueve una tarjeta de columna.
   *
   * El backend responde 200 aunque la etapa del CRM no se haya movido, y el motivo viene
   * en `message`. Se enseña como aviso y no como error: el estado local SI se guardo, y
   * decir "error" cuando la tarjeta se ha movido bien seria mentir.
   */
  const handleMove = async (id: string, status: ProposalStatus) => {
    setMovingIds((current) => new Set(current).add(id));
    try {
      const response = await moveProposal({ id, status }).unwrap();
      if (response.message) {
        dispatch(
          setToastMessage({ message: response.message, severity: "warning" })
        );
      }
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        t(
          "commercialProposals.board.moveFailed",
          "No se pudo mover la propuesta"
        );
      dispatch(setToastMessage({ message, severity: "error" }));
    } finally {
      setMovingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };

  const [retryProposal, { isLoading: isRetrying }] =
    useRetryCommercialProposalMutation();
  const [deleteProposal, { isLoading: isDeleting }] =
    useDeleteCommercialProposalMutation();

  const items = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;

  // La etapa se lee del CRM en vivo, no de nuestra tabla: la mueve el comercial
  // arrastrando tarjetas en Twenty y una copia local quedaría obsoleta al primer
  // arrastre. Solo se piden las de esta página.
  const opportunityIds = items
    .map((item) => item.crmOpportunityId)
    .filter((id): id is string => Boolean(id));

  const { data: stagesData, isError: stagesFailed } = useGetCrmStagesQuery(
    opportunityIds,
    { skip: opportunityIds.length === 0 }
  );

  // Un Map y no un find por fila: con 100 filas serían 100 recorridos del array.
  const stageByOpportunity = new Map(
    (stagesData?.data ?? []).map((entry) => [entry.opportunityId, entry.stage])
  );

  const handleRetry = async (id: string) => {
    try {
      const response = await retryProposal(id).unwrap();
      const synced = response.data?.crmStatus === "SYNCED";
      dispatch(
        setToastMessage({
          message: synced
            ? t("commercialProposals.list.retryOk", "Enviado al CRM")
            : (response.data?.crmError ??
              t(
                "commercialProposals.list.retryFailed",
                "El CRM sigue sin responder"
              )),
          severity: synced ? "success" : "error",
        })
      );
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "commercialProposals.list.retryFailed",
            "El CRM sigue sin responder"
          ),
          severity: "error",
        })
      );
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      const response = await deleteProposal(pendingDelete.id).unwrap();
      dispatch(
        setToastMessage({
          message: response.data?.crmOpportunityAlreadyGone
            ? t(
                "commercialProposals.list.deletedAlreadyGone",
                "Propuesta borrada. En el CRM ya no estaba."
              )
            : t("commercialProposals.list.deleted", "Propuesta borrada"),
          severity: "success",
        })
      );
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        t(
          "commercialProposals.list.deleteFailed",
          "No se pudo borrar la propuesta"
        );
      dispatch(setToastMessage({ message, severity: "error" }));
    } finally {
      setPendingDelete(null);
    }
  };

  const statusChip = (status: CommercialBriefingCrmStatus) => {
    const config: Record<
      string,
      { color: "success" | "warning" | "error"; label: string }
    > = {
      SYNCED: {
        color: "success",
        label: t("commercialProposals.status.synced", "En el CRM"),
      },
      PENDING: {
        color: "warning",
        label: t("commercialProposals.status.pending", "Pendiente"),
      },
      ERROR: {
        color: "error",
        label: t("commercialProposals.status.error", "Error de envío"),
      },
    };
    const entry = config[status] ?? config.PENDING;
    return <Chip size="small" color={entry.color} label={entry.label} />;
  };

  /**
   * La etapa de la oportunidad, leída del CRM.
   *
   * Tres estados distintos y a propósito, porque son tres problemas distintos:
   *   - sin oportunidad: la propuesta no ha llegado al CRM, y eso ya lo dice la columna
   *     de al lado, así que aquí no se repite;
   *   - el CRM no responde: se deja en blanco. Inventar un "desconocido" en rojo haría
   *     parecer roto el listado cuando lo que está caído es Twenty;
   *   - la oportunidad no vuelve en la respuesta: existía y ya no está en el CRM
   *     (borrada a mano), y eso sí merece un aviso.
   */
  const stageCell = (item: CommercialProposalListItem) => {
    if (!item.crmOpportunityId) return "—";
    if (stagesFailed || !stagesData) return "";

    if (!stageByOpportunity.has(item.crmOpportunityId)) {
      return (
        <Tooltip
          title={t(
            "commercialProposals.stageGone",
            "La oportunidad ya no está en el CRM. Puede haberse borrado a mano."
          )}
        >
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t("commercialProposals.stageGoneShort", "no está")}
          />
        </Tooltip>
      );
    }

    const stage = stageByOpportunity.get(item.crmOpportunityId);
    if (!stage) return "—";
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t(`commercialProposals.stage.${stage}`, stage)}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="h5">
              {t("commercialProposals.title", "Propuestas comerciales")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                "commercialProposals.subtitle",
                "La propuesta previa a la venta, con sus proyectos dentro."
              )}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, next) => {
                // MUI manda null al pulsar el que ya esta activo: sin esto, la vista
                // se quedaria sin valor y el tablero desapareceria.
                if (next) changeView(next);
              }}
            >
              <ToggleButton value="list">
                <Tooltip
                  title={t("commercialProposals.viewMode.list", "Listado")}
                >
                  <ViewListIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="board">
                <Tooltip
                  title={t("commercialProposals.viewMode.board", "Tablero")}
                >
                  <ViewColumnIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title={t("commercialProposals.refresh", "Recargar")}>
              <IconButton onClick={() => refetch()} disabled={isFetching}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/commercial-proposals/new")}
            >
              {t("commercialProposals.new.action", "Nueva propuesta")}
            </Button>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            alignItems={{ md: "center" }}
            flexWrap="wrap"
          >
            <TextField
              size="small"
              label={t("commercialProposals.filters.search", "Buscar")}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder={t(
                "commercialProposals.filters.searchPlaceholder",
                "Número, cliente, marca o nombre"
              )}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>
                {t("commercialProposals.businessUnit", "Unidad de negocio")}
              </InputLabel>
              <Select
                value={businessUnit}
                label={t(
                  "commercialProposals.businessUnit",
                  "Unidad de negocio"
                )}
                onChange={(event) => {
                  setBusinessUnit(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("commercialProposals.filters.all", "Todas")}
                </MenuItem>
                <MenuItem value="HOUSE_GROUP">House Group</MenuItem>
                <MenuItem value="BEAPOLO">BeApolo</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>
                {t("commercialProposals.filters.crmStatus", "Estado del CRM")}
              </InputLabel>
              <Select
                value={crmStatus}
                label={t(
                  "commercialProposals.filters.crmStatus",
                  "Estado del CRM"
                )}
                onChange={(event) => {
                  setCrmStatus(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("commercialProposals.filters.all", "Todas")}
                </MenuItem>
                <MenuItem value="SYNCED">
                  {t("commercialProposals.status.synced", "En el CRM")}
                </MenuItem>
                <MenuItem value="PENDING">
                  {t("commercialProposals.status.pending", "Pendiente")}
                </MenuItem>
                <MenuItem value="ERROR">
                  {t("commercialProposals.status.error", "Error de envío")}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>
                {t("commercialProposals.filters.year", "Año")}
              </InputLabel>
              <Select
                value={year}
                label={t("commercialProposals.filters.year", "Año")}
                onChange={(event) => {
                  setYear(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("commercialProposals.filters.allMasc", "Todos")}
                </MenuItem>
                {(filterOptions?.years ?? []).map((option) => (
                  <MenuItem key={option} value={String(option)}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>
                {t(
                  "commercialProposals.responsableComercial",
                  "Responsable comercial"
                )}
              </InputLabel>
              <Select
                value={responsableComercial}
                label={t(
                  "commercialProposals.responsableComercial",
                  "Responsable comercial"
                )}
                onChange={(event) => {
                  setResponsableComercial(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("commercialProposals.filters.allMasc", "Todos")}
                </MenuItem>
                {(filterOptions?.responsablesComerciales ?? []).map(
                  (person) => (
                    <MenuItem key={person.id} value={person.id}>
                      {person.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>
                {t(
                  "commercialProposals.responsablePropuesta",
                  "Responsable de propuesta"
                )}
              </InputLabel>
              <Select
                value={responsablePropuesta}
                label={t(
                  "commercialProposals.responsablePropuesta",
                  "Responsable de propuesta"
                )}
                onChange={(event) => {
                  setResponsablePropuesta(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("commercialProposals.filters.allMasc", "Todos")}
                </MenuItem>
                {(filterOptions?.responsablesPropuesta ?? []).map((person) => (
                  <MenuItem key={person.id} value={person.id}>
                    {person.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>
                {t("commercialProposals.filters.house", "House")}
              </InputLabel>
              <Select
                value={house}
                label={t("commercialProposals.filters.house", "House")}
                onChange={(event) => {
                  setHouse(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  {t("commercialProposals.filters.all", "Todas")}
                </MenuItem>
                {Object.entries(HOUSE_LABEL).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {view === "list" && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>
                  {t("commercialProposals.filters.status", "Estado")}
                </InputLabel>
                <Select
                  value={status}
                  label={t("commercialProposals.filters.status", "Estado")}
                  onChange={(event) => {
                    setStatus(event.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">
                    {t("commercialProposals.filters.allMasc", "Todos")}
                  </MenuItem>
                  {BOARD_COLUMNS.map((column) => (
                    <MenuItem key={column} value={column}>
                      {t(`commercialProposals.board.${column}`, column)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {view === "list" && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={hideArchived}
                    onChange={(event) => {
                      setHideArchived(event.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label={t(
                  "commercialProposals.filters.hideArchived",
                  "Ocultar archivadas"
                )}
              />
            )}
            {hasFilters && (
              <Button size="small" onClick={clearFilters}>
                {t("commercialProposals.filters.clear", "Limpiar filtros")}
              </Button>
            )}
          </Stack>

          {isError && (
            <Alert severity="error">
              {t(
                "commercialProposals.loadFailed",
                "No se pudo cargar el listado. Prueba a recargar."
              )}
            </Alert>
          )}

          {view === "board" && (
            <ProposalBoard
              items={items}
              onMove={handleMove}
              moving={movingIds}
              onOpen={(id) => navigate(`/commercial-proposals/${id}`)}
            />
          )}

          {view === "list" && (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("commercialProposals.columns.number", "Número")}
                      </TableCell>
                      <TableCell>
                        {t("commercialProposals.columns.client", "Cliente")}
                      </TableCell>
                      <TableCell>
                        {t("commercialProposals.columns.name", "Propuesta")}
                      </TableCell>
                      <TableCell align="center">
                        {t("commercialProposals.columns.projects", "Proyectos")}
                      </TableCell>
                      <TableCell align="right">
                        {t("commercialProposals.columns.total", "Presupuesto")}
                      </TableCell>
                      <TableCell>
                        {t("commercialProposals.columns.deadline", "Entrega")}
                      </TableCell>
                      <TableCell>
                        {t("commercialProposals.columns.owner", "Comercial")}
                      </TableCell>
                      <TableCell>
                        {t("commercialProposals.columns.stage", "Etapa")}
                      </TableCell>
                      <TableCell>
                        {t("commercialProposals.columns.crm", "CRM")}
                      </TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isFetching && items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <CircularProgress size={24} sx={{ my: 3 }} />
                        </TableCell>
                      </TableRow>
                    )}

                    {!isFetching && items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ my: 3 }}
                          >
                            {t(
                              "commercialProposals.empty",
                              "No hay propuestas con esos filtros"
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {items.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/commercial-proposals/${item.id}`)
                        }
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              // Archivada: se lee, pero no compite con las vigentes.
                              color: item.isCurrentVersion
                                ? undefined
                                : "text.secondary",
                            }}
                          >
                            {item.proposalNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {BUSINESS_UNIT_LABEL[item.businessUnit] ??
                              item.businessUnit}
                          </Typography>
                          {!item.isCurrentVersion && (
                            <Box mt={0.5}>
                              <Tooltip
                                title={
                                  item.supersededBy
                                    ? t(
                                        "commercialProposals.archivedBy",
                                        "Sustituida por {{number}}. Sigue aquí para poder ver de dónde viene la vigente, pero no sale en el tablero.",
                                        {
                                          number:
                                            item.supersededBy.proposalNumber,
                                        }
                                      )
                                    : t(
                                        "commercialProposals.archivedPlain",
                                        "No es la versión vigente de este plan."
                                      )
                                }
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  icon={<ArchiveOutlinedIcon />}
                                  label={
                                    item.supersededBy
                                      ? t(
                                          "commercialProposals.archivedShort",
                                          "archivada · V{{version}}",
                                          { version: item.supersededBy.version }
                                        )
                                      : t(
                                          "commercialProposals.archivedOnly",
                                          "archivada"
                                        )
                                  }
                                  // Llevar a la vigente es lo que se quiere hacer al
                                  // ver una archivada, así que el chip es el atajo.
                                  onClick={
                                    item.supersededBy
                                      ? (event) => {
                                          event.stopPropagation();
                                          navigate(
                                            `/commercial-proposals/${item.supersededBy?.id}`
                                          );
                                        }
                                      : undefined
                                  }
                                />
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.brandName ?? item.clientName}
                          {item.brandName && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {item.clientName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell align="center">
                          {item.projectCount}
                        </TableCell>
                        <TableCell align="right">
                          {euro.format(item.totalBudget)}
                        </TableCell>
                        <TableCell>{item.deliveryDeadline}</TableCell>
                        <TableCell>
                          {item.responsableComercialName ?? "—"}
                        </TableCell>
                        <TableCell>{stageCell(item)}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            {statusChip(item.crmStatus)}
                            {item.crmAccountUnitMissing && (
                              <Tooltip
                                title={t(
                                  "commercialProposals.unitMissing",
                                  "Emitida sin unidad de cuenta en el CRM"
                                )}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={t(
                                    "commercialProposals.unitMissingShort",
                                    "sin unidad"
                                  )}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Stack direction="row" justifyContent="flex-end">
                            {item.crmUrl && (
                              <Tooltip
                                title={t(
                                  "commercialProposals.openInCrm",
                                  "Abrir en el CRM"
                                )}
                              >
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={item.crmUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <LaunchIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {item.crmStatus !== "SYNCED" && (
                              <Tooltip
                                title={t(
                                  "commercialProposals.retry",
                                  "Reintentar el envío al CRM"
                                )}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleRetry(item.id)}
                                  disabled={isRetrying}
                                >
                                  <SyncIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip
                                title={t(
                                  "commercialProposals.delete",
                                  "Borrar"
                                )}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => setPendingDelete(item)}
                                  disabled={isDeleting}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[20, 50, 100]}
              />
            </>
          )}
        </Paper>
      </Box>

      <ConfirmationModal
        open={pendingDelete !== null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title={t("commercialProposals.deleteTitle", "Borrar propuesta")}
        message={t(
          "commercialProposals.deleteMessage",
          'Se borrará la propuesta "{{number}}" y su oportunidad en el CRM. El cliente y su número se quedan en el registro.',
          { number: pendingDelete?.proposalNumber ?? "" }
        )}
        confirmLabel={t("commercialProposals.delete", "Borrar")}
      />
    </Box>
  );
};

export default CommercialProposals;
